import {isFeatureEnabled} from '@github-ui/feature-flags'
import {LABELS} from '@github-ui/issues-shared/labels'
import {VALUES} from '@github-ui/issues-shared/values'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {BookmarkIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, NavList} from '@primer/react'
import {useEffect, useMemo} from 'react'
import {graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {useSetSavedViewsCount} from '../../contexts/QueryContext'
import useKnownShortcuts from '../../hooks/use-known-views'
import {useViewsNav} from '../../hooks/viewsNav'
import type {SavedViewRoute} from '../../types/views-types'
import type {SavedViewsQuery} from './__generated__/SavedViewsQuery.graphql'
import type {SavedViewsShortcutsFragment$key} from './__generated__/SavedViewsShortcutsFragment.graphql'
import {CreateSavedView, useCreateSavedView} from './CreateSavedView'
import {SavedViewRow} from './SavedViewRow'
import styles from './SavedViews.module.css'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const SavedViewsGraphqlQuery = graphql`
  query SavedViewsQuery {
    viewer {
      dashboard {
        ...SavedViewsShortcutsFragment
      }
    }
  }
`

type SavedViewsProps = {
  savedViewsRef: PreloadedQuery<SavedViewsQuery>
}
type SavedViewsInternalProps = {
  savedViewsRef: SavedViewsShortcutsFragment$key
}

export function SavedViews({savedViewsRef}: SavedViewsProps) {
  const preloadedData = usePreloadedQuery<SavedViewsQuery>(SavedViewsGraphqlQuery, savedViewsRef)
  return preloadedData.viewer.dashboard ? <SavedViewsInternal savedViewsRef={preloadedData.viewer.dashboard} /> : null
}

function SavedViewsInternal({savedViewsRef}: SavedViewsInternalProps) {
  const {knownViews} = useKnownShortcuts()
  const setSavedViewsCount = useSetSavedViewsCount()
  const {isExpanded} = useCollapsibleSidebarContext()

  const shortcutData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SavedViewsShortcutsFragment on UserDashboard {
        shortcuts(first: 25, searchTypes: [ISSUES]) {
          totalCount
          nodes {
            id
            name
            query
            ...SavedViewRow
          }
        }
      }
    `,
    savedViewsRef,
  )

  const savedViews = useMemo(() => shortcutData.shortcuts.nodes || [], [shortcutData.shortcuts.nodes])

  useEffect(() => {
    setSavedViewsCount(shortcutData.shortcuts.totalCount)
  }, [shortcutData, setSavedViewsCount])

  const savedViewRoutes: SavedViewRoute[] = useMemo(
    () =>
      savedViews.filter(isDefined).map((view, index) => {
        return {
          id: view.id,
          name: view.name,
          query: view.query,
          position: index + knownViews.length + 1,
        }
      }),
    [knownViews.length, savedViews],
  )

  const viewKeys = useMemo(() => savedViewRoutes?.map(route => route.position.toString()), [savedViewRoutes])
  useViewsNav(savedViewRoutes, viewKeys, knownViews.length)

  const savedViewRows =
    savedViews.length > 0
      ? savedViews
          .filter(isDefined)
          .map((savedView, index) => <SavedViewRow key={savedView.id} savedView={savedView} position={index + 1} />)
      : null

  const createDisabled = savedViews.length >= VALUES.viewsPageSize
  const {label: createLabel, onClick: onCreateView, maxViewsDialog} = useCreateSavedView(createDisabled)

  if (isFeatureEnabled('global_issues_filtered_list_layout') && !isExpanded) {
    return (
      <>
        <CollapsibleNavList aria-label={LABELS.viewsTitle} className={styles.savedViewsList}>
          <CollapsibleNavList.Group
            icon={BookmarkIcon}
            title={LABELS.viewsTitle}
            trailingAction={
              <ActionList.GroupHeading.TrailingAction
                as="button"
                icon={PlusIcon}
                label={createLabel}
                onClick={onCreateView}
                tooltipDirection="e"
              />
            }
          >
            {savedViewRows}
          </CollapsibleNavList.Group>
        </CollapsibleNavList>
        {maxViewsDialog}
      </>
    )
  }

  return (
    <>
      <div className={`d-flex flex-items-center ${styles.headerRow}`}>
        <span className="flex-1 ml-2">{LABELS.viewsTitle}</span>
        <CreateSavedView disabled={createDisabled} />
      </div>
      <NavList aria-label={LABELS.viewsTitle} className={styles.savedViewsList}>
        {savedViewRows}
      </NavList>
    </>
  )
}

function isDefined<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null
}
